import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { eq, or } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import ButtonOptions from './button-options.gts';
import safeFormatDate from '../../helpers/safe-format-date.ts';
import safeFormatTime from '../../helpers/safe-format-time.ts';
import type EligibilityFileJobLogModel from '../../models/eligibility-file-job-log.ts';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import Chip from 'tio-reimagined/components/chip';
import { HStack, VStack, Section } from 'tio-ui/components/layout';
import { PhArrowRight } from 'ember-phosphor-icons';

interface EligibilityFilesEligibilityCardSignature {
  Args: {
    item: EligibilityFileJobLogModel;
    showRoute: string;
    indexRoute: string;
    abortProcess?: (item: EligibilityFileJobLogModel) => void;
    beginProcess?: (item: EligibilityFileJobLogModel) => void;
    userId?: string;
  };
}

export default class EligibilityFilesEligibilityCard extends Component<EligibilityFilesEligibilityCardSignature> {
  @service declare router: RouterService;
  @service declare intl: IntlService;

  get isAdmin() {
    // TODO: There are other services to figure this out. If routes are moved around this would break.
    return !!this.router.currentRouteName?.includes('authenticated.eligibility.index');
  }

  get isAborted() {
    return this.args.item.hasAborted;
  }

  get processTotal() {
    return this.args.item.isLoading ? 'Loading' : this.args.item.totalProcessed;
  }

  get unchangedTotal() {
    return this.args.item.isLoading ? 'Loading' : this.args.item.totalUnchanged;
  }

  get processCount() {
    return this.args.item.isLoading ? undefined : this.args.item.counts;
  }

  get previewCount() {
    return this.args.item.isLoading ? undefined : this.args.item.previewCounts;
  }

  get autoProcessEnabled() {
    return this.args.item.autoProcessEnabled;
  }

  countPercentage = (previewCount: number = 0, processCount: number = 0) => {
    if (previewCount === 0) {
      return 0;
    }
    return Math.floor((processCount / previewCount) * 100).toString() + '%';
  };

  showAbortStatus = (status: string) => {
    return status.toLowerCase().replace(/_/g, ' ');
  };

  convertToReadableName = (status: string) => {
    return this.intl.t('common.eligibility_file_upload.status.' + status) || 'ALL';
  };

  get statusIntent() {
    const status = this.args.item.status?.toLowerCase();
    if (status?.includes('completed')) return 'success';
    if (status?.includes('processing') || status?.includes('running')) return 'warning';
    if (status?.includes('failed') || status?.includes('error') || status?.includes('abort'))
      return 'danger';
    return 'default';
  }

  get strategyDisplay() {
    const strategy = this.args.item.strategy;
    if (eq(strategy, 'MANUAL')) return this.intl.t('eligibility_files.manual');
    if (eq(strategy, 'MANUAL_FORCE_UPDATE')) return this.intl.t('eligibility_files.forced');
    if (eq(strategy, 'MANUAL_EXTERNAL_SERVICE')) return this.intl.t('eligibility_files.external');
    return this.intl.t('eligibility_files.automatic');
  }

  <template>
    <Section class="border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <:header>
        <div>
          {{#unless this.autoProcessEnabled}}
            <div
              class="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded mb-2"
            >
              {{t "eligibility_files.auto_process_disabled"}}
            </div>
          {{/unless}}
          <LinkTo
            @route={{@showRoute}}
            @model={{@item.id}}
            class="text-lg font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
            title={{@item.fileName}}
          >
            {{@item.fileName}}
            <PhArrowRight @size={{16}} class="inline-block ml-1" />
          </LinkTo>
        </div>
      </:header>

      <:body>
        <VStack>
          <div class="flex justify-between items-start">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm flex-1">
              {{#if this.isAdmin}}
                <div>
                  <span class="font-medium text-gray-700">{{t
                      "eligibility_files.partner_label"
                    }}</span>
                  <span class="ml-2 text-gray-900">{{@item.company.name}}</span>
                </div>
              {{/if}}
              <div>
                <span class="font-medium text-gray-700">{{t
                    "eligibility_files.strategy_label"
                  }}</span>
                <span class="ml-2 text-gray-900">{{this.strategyDisplay}}</span>
              </div>
              <div>
                <span class="font-medium text-gray-700">{{t
                    "eligibility_files.started_label"
                  }}</span>
                <span class="ml-2 text-gray-900">
                  {{safeFormatDate @item.startTime}},
                  {{safeFormatTime @item.startTime}}
                </span>
              </div>
              {{#if @item.endTime}}
                <div>
                  <span class="font-medium text-gray-700">{{t
                      "eligibility_files.completed_label"
                    }}</span>
                  <span class="ml-2 text-gray-900">
                    {{safeFormatDate @item.endTime}},
                    {{safeFormatTime @item.endTime}}
                  </span>
                </div>
              {{/if}}
            </div>
            <div class="shrink-0">
              <Chip @intent={{this.statusIntent}}>
                {{#if this.isAborted}}
                  {{this.showAbortStatus @item.status}}
                {{else}}
                  {{this.convertToReadableName @item.status}}
                {{/if}}
              </Chip>
            </div>
          </div>

          {{#if this.isAdmin}}
            <div class="bg-gray-50 rounded-lg p-3">
              <div class="text-sm font-medium text-gray-700 mb-2">{{t
                  "eligibility_files.processing_summary"
                }}</div>
              <HStack>
                <div>
                  <span
                    class="font-semibold text-green-600 dark:text-green-400"
                  >{{this.processTotal}}</span>
                  <span class="text-gray-600 ml-1">{{t "eligibility_files.completed_count"}}</span>
                </div>
                <div>
                  <span class="font-semibold text-gray-600">{{this.unchangedTotal}}</span>
                  <span class="text-gray-600 ml-1">{{t "eligibility_files.unchanged_count"}}</span>
                </div>
              </HStack>
            </div>

            {{#if this.processCount}}
              <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <div class="text-center p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded">
                  <div class="font-semibold text-emerald-600 dark:text-emerald-400">
                    {{this.processCount.created}}/{{this.previewCount.created}}
                  </div>
                  <div class="text-xs text-emerald-700 dark:text-emerald-300">{{t
                      "eligibility_files.created_label"
                    }}</div>
                </div>

                <div class="text-center p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
                  <div class="font-semibold text-amber-600 dark:text-amber-400">
                    {{this.processCount.updated}}/{{this.previewCount.updated}}
                  </div>
                  <div class="text-xs text-amber-700 dark:text-amber-300">{{t
                      "eligibility_files.updated_label"
                    }}</div>
                </div>

                <div class="text-center p-2 bg-red-50 dark:bg-red-900/20 rounded">
                  <div class="font-semibold text-red-600 dark:text-red-400">
                    {{this.processCount.deactivated}}/{{this.previewCount.deactivated}}
                  </div>
                  <div class="text-xs text-red-700 dark:text-red-300">{{t
                      "eligibility_files.deactivated_label"
                    }}</div>
                </div>

                <div class="text-center p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
                  <div class="font-semibold text-purple-600 dark:text-purple-400">
                    {{this.processCount.reactivated}}/{{this.previewCount.reactivated}}
                  </div>
                  <div class="text-xs text-purple-700 dark:text-purple-300">{{t
                      "eligibility_files.reactivated_label"
                    }}</div>
                </div>
              </div>
            {{/if}}
          {{/if}}
        </VStack>
      </:body>

      <:actions>
        {{#if this.isAdmin}}
          <ButtonOptions
            @indexRoute={{@indexRoute}}
            @status={{@item.status}}
            @companyId={{or @item.company.id ""}}
            @legacyId={{@item.company.legacyId}}
            @fileName={{@item.fileName}}
            @abortProcess={{@abortProcess}}
            @beginProcess={{@beginProcess}}
            @item={{@item}}
            @currentUserId={{or @userId ""}}
          />
        {{/if}}
      </:actions>
    </Section>
  </template>
}
